import type { DaySequence, DaySequenceStatus } from '@core/generated/gql-types';
import Button from '@/components/Button';
import { formatEnumLabel } from '@/modules/payroll/utils/payrollFormat.utils';

const STATUS_CHANGE_DESCRIPTIONS: Partial<Record<DaySequenceStatus, string>> = {
  READY_FOR_PAY: 'Approve the shift so it is included in the next pay run.',
  ESCALATED_TO_TENANT: 'Dispute the shift for the tenant to resolve.',
};

type DaySequenceStatusModalProps = {
  sequence: DaySequence | null;
  /** Statuses the user is permitted to set. */
  options: DaySequenceStatus[];
  onSelect: (sequence: DaySequence, status: DaySequenceStatus) => void;
  onClose: () => void;
};

const DaySequenceStatusModal = ({
  sequence,
  options,
  onSelect,
  onClose,
}: DaySequenceStatusModalProps) => {
  if (!sequence) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="day-sequence-status-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h3
          id="day-sequence-status-title"
          className="text-lg font-semibold text-gray-900"
        >
          Change status
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Currently {formatEnumLabel(sequence.status)}
        </p>
        <div className="mt-4 flex flex-col gap-2">
          {options.map((status) => (
            <button
              key={status}
              type="button"
              disabled={sequence.status === status}
              onClick={() => onSelect(sequence, status)}
              className="rounded-md border border-gray-200 px-4 py-3 text-left hover:border-[#03406A] hover:bg-[#03406A]/5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="block text-sm font-medium text-gray-900">
                {formatEnumLabel(status)}
              </span>
              <span className="block text-xs text-gray-500">
                {STATUS_CHANGE_DESCRIPTIONS[status]}
              </span>
            </button>
          ))}
        </div>
        <div className="mt-6 flex justify-end">
          <Button variant="outline" className="py-2 text-sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DaySequenceStatusModal;
